import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import oktaAuth from '../modules/auth/OktaAuth';

// Define a service using a base URL and expected endpoints
export const dataProtectionApi = createApi({
    reducerPath: 'dataProtectionApi',
    tagTypes: ['Customers', 'Assessment', 'Estimate', 'ERR', 'Groups', 'Configs'],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_DATA_PROTECTION_URL || `https://innovation-portal-data-security.azure-api.net/dev/`,
        prepareHeaders: (headers) => {
            // , { getState }
            // const token = (getState() as RootState).auth.token;
            const idToken = oktaAuth.getIdToken();
            // If we have a token set in state, let's assume that we should be passing it.
            if (idToken) {
                headers.set('authorization', `Bearer ${idToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        getCustomers: builder.query({
            query: () => `GetCustomers`,
            providesTags: ['Customers'],
        }),
        addCustomer: builder.mutation({
            query: (body) => ({
                url: `GetCustomers`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Customers'],
        }),
        addHeader: builder.mutation({
            query: (input) => ({
                url: `CreateAssessmentHeader`,
                method: 'POST',
                body: { ...input },
            }),
        }),
        getHeader: builder.query({
            query: (id) => `CreateAssessmentHeader?assessment_id=${id}`,
            transformResponse: (response) => Object.values(response)[0],
        }),
        getAssessments: builder.mutation({
            query: (input) => ({
                url: `AssessmentDetails`,
                method: 'POST',
                body: { ...input },
            }),
        }),
        getAssessmentById: builder.query({
            query: (input) => ({
                url: `AssessmentDetails`,
                method: 'POST',
                body: { ...input },
            }),
            providesTags: (result, error, arg) => (result.id ? [{ type: 'Assessment', id: result.id }, 'Assessment'] : ['Assessment']),
        }),
        getEstimate: builder.query({
            query: (input) => ({
                url: `AssessmentDetails`,
                method: 'POST',
                body: { ...input },
            }),
            providesTags: (result, error, arg) => (result ? [{ type: 'Estimate', id: arg.assessment_id }, 'Estimate'] : ['Estimate']),
        }),
        saveAssessment: builder.mutation({
            query: (body) => ({
                url: `SaveSubmit?savetype=save`,
                method: 'POST',
                body,
            }),
        }),
        submitAssessment: builder.mutation({
            query: (body) => ({
                url: `SaveSubmit?savetype=submit`,
                method: 'POST',
                body,
            }),
        }),
        getConfigs: builder.mutation({
            query: (id) => `Config?service_id=${id}`,
            providesTags: ['Configs'],
            keepUnusedDataFor: 1,
        }),
        updateConfig: builder.mutation({
            query: (body) => ({
                url: `Config`,
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['Configs'],
        }),
        getAssessmentEdit: builder.query({
            query: (id) => `Question/${id}`,
        }),
        updateAssessmentEdit: builder.mutation({
            query: ({ id, body }) => ({
                url: `Question/${id}`,
                method: 'PUT',
                body,
            }),
        }),
        updateAssessmentStatus: builder.mutation({
            query: ({ status, body }) => ({
                url: `SaveSubmit?savetype=${status}`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Assessment', id: arg.body.assessment_id }],
        }),
        validateERR: builder.mutation({
            query: (err) => ({
                url: `ERR?err=${err}`,
                method: 'GET',
            }),
            providesTags: (result, error, arg) => (result.id ? [{ type: 'ERR', id: arg }, 'ERR'] : ['ERR']),
        }),
        updateNote: builder.mutation({
            query: (body) => ({
                url: `SaveSubmit?savetype=sensitive`,
                method: 'POST',
                body,
            }),
            invalidatesTags: (result, error, arg) => [{ type: 'Assessment', id: arg.assessment_id }],
        }),
        getGroups: builder.query({
            query: () => `UserGroup?type=groups&group_id=all`,
            providesTags: ['Groups'],
        }),
        createUpdateGroup: builder.mutation({
            query: (body) => ({
                url: `UserGroup?type=groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Groups'],
        }),
        deleteGroup: builder.mutation({
            query: (id) => ({
                url: `UserGroup?type=groups&group_id=${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: ['Groups'],
        }),
        getServicePermissions: builder.query({
            query: () => `UserGroup?type=permissions`,
        }),
        getUsers: builder.query({
            query: () => `UserGroup?type=users&email_id=all`,
            providesTags: ['Users'],
        }),

        getUserByEmail: builder.query({
            query: (email) => `UserGroup?type=users&email_id=${email}`,
        }),
        getCurrentUser: builder.query({
            query: () => `UserGroup?type=users`,
            transformResponse: (response) => response[0],
        }),
        editUserGroup: builder.mutation({
            query: (body) => ({
                url: `UserGroup?type=user-groups`,
                method: 'POST',
                body,
            }),
            invalidatesTags: ['Groups', 'Users'],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCustomersQuery,
    useAddCustomerMutation,
    useAddHeaderMutation,
    useGetHeaderQuery,
    useGetAssessmentsMutation,
    useGetAssessmentByIdQuery,
    useGetEstimateQuery,
    useSaveAssessmentMutation,
    useSubmitAssessmentMutation,
    //useGetConfigsQuery,
    useUpdateConfigMutation,
    useUpdateAssessmentStatusMutation,
    useValidateERRMutation,
    useUpdateNoteMutation,
    useGetGroupsQuery,
    useCreateUpdateGroupMutation,
    useDeleteGroupMutation,
    useGetServicePermissionsQuery,
    useGetUsersQuery,
    useGetUserByEmailQuery,
    useGetCurrentUserQuery,
    useLazyGetCurrentUserQuery,
    useEditUserGroupMutation,
    useGetConfigsMutation,
    useGetAssessmentEditQuery,
    useUpdateAssessmentEditMutation,
} = dataProtectionApi;
